import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Check, Gift } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { toast } from "sonner";

export default function RewardRedeemDialog({ open, onOpenChange, reward, pastaFlavours, wingFlavours }) {
  const [pasta, setPasta] = useState(null);
  const [wing, setWing] = useState(null);
  const { addItem } = useCart();

  React.useEffect(() => { if (open) { setPasta(null); setWing(null); } }, [open]);

  if (!reward) return null;

  const isCombo = reward.type === "free_combo";
  const ready = isCombo ? !!pasta && !!wing : !!wing;

  const apply = () => {
    if (!ready) return;
    if (isCombo) {
      addItem({
        is_combo: true, // treated like a combo line internally
        reward_id: reward.id,
        reward_type: "free_combo",
        combo_pasta: pasta,
        combo_wings: wing,
        name: `🎁 FREE COMBO: ${pastaFlavours[pasta].name} + 5 ${wingFlavours[wing].name} Wings`,
        unit_price: 0,
        quantity: 1,
      });
    } else {
      addItem({
        is_combo: false,
        reward_id: reward.id,
        reward_type: "free_5_wings",
        combo_wings: wing,
        name: `🎁 FREE 5 Wings — ${wingFlavours[wing].name}`,
        unit_price: 0,
        quantity: 1,
      });
    }
    toast.success("Reward added to basket");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-black border-2 border-gold rounded-none max-w-2xl p-0" data-testid="reward-dialog">
        <div className="p-6">
          <DialogHeader>
            <DialogTitle className="font-heading text-gold uppercase tracking-tight text-3xl flex items-center gap-2">
              <Gift className="h-7 w-7" /> {reward.label}
            </DialogTitle>
            <DialogDescription className="text-cream/70 font-body">
              {isCombo ? "Pick your free pasta and wings flavour." : "Pick your free wings flavour."}
            </DialogDescription>
          </DialogHeader>

          {isCombo && (
            <>
              <div className="mt-4 font-heading uppercase tracking-widest text-cream/70 text-sm">Step 1 — Pasta</div>
              <div className="mt-2 grid sm:grid-cols-2 gap-2">
                {Object.entries(pastaFlavours).map(([k, v]) => (
                  <button key={k} onClick={() => setPasta(k)}
                    className={`text-left p-3 border-2 ${pasta === k ? "border-gold bg-gold/10" : "border-border hover:border-gold/60"}`}
                    data-testid={`reward-pasta-${k}`}
                  >
                    <div className="font-heading uppercase text-cream tracking-tight text-lg leading-none">{v.name}</div>
                    {pasta === k && <Check className="h-4 w-4 text-gold mt-1" />}
                  </button>
                ))}
              </div>
            </>
          )}

          <div className={`${isCombo ? "mt-5" : "mt-4"} font-heading uppercase tracking-widest text-cream/70 text-sm`}>
            {isCombo ? "Step 2 — Wings" : "Pick your wings flavour"}
          </div>
          <div className="mt-2 grid sm:grid-cols-2 gap-2">
            {Object.entries(wingFlavours).map(([k, v]) => (
              <button key={k} onClick={() => setWing(k)}
                className={`text-left p-3 border-2 ${wing === k ? "border-gold bg-gold/10" : "border-border hover:border-gold/60"}`}
                data-testid={`reward-wing-${k}`}
              >
                <div className="font-heading uppercase text-cream tracking-tight text-lg leading-none">{v.name}</div>
                {wing === k && <Check className="h-4 w-4 text-gold mt-1" />}
              </button>
            ))}
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => onOpenChange(false)} className="btn-outline-poster" data-testid="reward-cancel">Cancel</button>
            <button onClick={apply} disabled={!ready} className={`btn-poster ${!ready ? "opacity-40 cursor-not-allowed" : ""}`} data-testid="reward-apply">
              Add Free Item
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
